import type React from "react"
import { createElement, useRef, useState, useEffect } from "react"

// * Experimental: This component forces React to skip hydrating the children and use the server-side rendered HTML as is.

const useStaticContent = () => {
  const ref = useRef<Element>(null)
  const [render, setRender] = useState(import.meta.env.SSR)

  useEffect(() => {
    // Check if the innerHTML is empty as component might not have been server-side rendered in case of client side
    // navigation or SSR failure.
    if (ref.current && ref.current.innerHTML === "") {
      setRender(true)
    }
  }, [])

  return [render, ref]
}

function StaticContent<P extends React.HTMLAttributes<T>, T extends HTMLElement>({
  children,
  element = "div",
  ...props
}: {
  children: React.ReactNode
  element?: keyof React.ReactHTML
} & (P | null)) {
  const [render, ref] = useStaticContent()

  if (render) {
    return createElement(
      element,
      {
        ...props,
      },
      children
    )
  }

  // React will keep the current content of dangerouslySetInnerHTML when the content being set is different.
  return createElement(element, {
    ...props,
    ref,
    suppressHydrationWarning: true,
    dangerouslySetInnerHTML: { __html: "" },
  })
}

export default StaticContent
